import {CLIENT_PERMISSION_CODES_CONSTANTS} from '../constants/constants';
import {
  PermissionsByClient,
  UserRolesData,
} from '../redux/slices/userRoleSlice';
import {RootState} from '../redux/store';
import {UserRoleCode} from '../types/user-role-codes/roleCodes';

export const buildClientPermissionMap = (
  userRoles: UserRolesData[]
): PermissionsByClient => {
  return userRoles.reduce<PermissionsByClient>((acc, userRole) => {
    const clientId = userRole?.client?.clientId;

    if (!clientId) return acc;

    if (!acc[clientId]) {
      acc[clientId] = {};
    }

    userRole?.userRolePermissionGroup?.forEach((group) => {
      group.permissionGroup?.permissions?.forEach((permission) => {
        acc[clientId][permission.permissionCode] = true;
      });
    });

    return acc;
  }, {});
};

export const selectHasClientPermission = (
  state: RootState,
  clientId: string,
  permissionCode: string
): boolean => {
  const {loggedInUserRoles, permissionsByClient} = state.userRole;

  const isSystemAdmin = loggedInUserRoles.some(
    (role) => role.role.roleCode === UserRoleCode.systemAdminUserRole
  );

  if (isSystemAdmin) {
    return true;
  }

  const isClientAdmin = loggedInUserRoles.some(
    (role) =>
      role.role.roleCode === UserRoleCode.clientAdminUserRole &&
      role.client?.clientId === clientId
  );

  if (isClientAdmin) {
    return true;
  }

  return !!permissionsByClient?.[clientId]?.[permissionCode];
};

export const selectCanAddClientToUser = (state: RootState): boolean => {
  const {loggedInUserRoles, userRoles} = state.userRole;

  const isSystemAdmin = loggedInUserRoles.some(
    (role) => role.role.roleCode === UserRoleCode.systemAdminUserRole
  );

  if (isSystemAdmin) {
    return true;
  }

  const assignedClientIds = new Set(
    userRoles.map((role) => role.client.clientId)
  );

  const manageableClientIds = new Set(
    loggedInUserRoles
      .map((role) => role.client?.clientId)
      .filter(
        (clientId): clientId is string =>
          !!clientId &&
          selectHasClientPermission(
            state,
            clientId,
            CLIENT_PERMISSION_CODES_CONSTANTS.USERS_VIEW
          )
      )
  );

  return [...manageableClientIds].some(
    (clientId) => !assignedClientIds.has(clientId)
  );
};
